import { AUDIT_STEP_RESULT } from "../../../constant/agenticPlatform";

import { agentStepEvents, attemptOf, currentAgentSteps, restartEvents } from "./runStatuses";

/**
 * The run as the modal renders it: one entry per ATTEMPT, each carrying the
 * steps that ran in it.
 *
 * WHY ATTEMPTS ARE THE UNIT. A restart re-runs the whole journey, so the log
 * holds the same step name once per attempt. Collapsing the log run-wide — which
 * is what `currentAgentSteps` does, correctly, for "what is true NOW" — keeps
 * only the newest copy, which silently DELETES the earlier attempt's row. On a
 * real run that rendered as
 *
 *     manual fill -> consent -> [restarted] -> eligibility
 *
 * because eligibility ran in both attempts and only the second survived, while
 * manual fill ran once and stayed put. Every row was in order; half the rows
 * were missing.
 *
 * So the timeline collapses per `(attempt, run, stepName)` and the state
 * readers keep collapsing run-wide. Two collapses, two questions: "what
 * happened" and "what is true now".
 *
 * Attempts are SEQUENTIAL, so they render in time order with the restart
 * between them. Parallel work — an enrollment fan-out, one child per program —
 * is grouped by program instead, because interleaving concurrent branches by
 * timestamp reads as one jumbled sequence rather than N stories.
 */

/**
 * Newest event per step WITHIN one attempt, superseded rows dropped — the
 * SHARED collapse, re-sorted ascending because a timeline reads forwards.
 *
 * `currentAgentSteps` and not a local copy: the dedupe key is the load-bearing
 * part, and the reason it is `(agenticRunId, stepName)` rather than the step
 * name alone is documented at `latestStepEvents` (an enrollment fan-out parks
 * one child per program, so collapsing on the name hides pending ones). A
 * second copy here would take a correction to that key, or to the SUPERSEDED
 * rule, in one place only — and the timeline would start dropping rows while
 * the state readers stayed right.
 */
const collapseAttempt = (events) => [...currentAgentSteps(events)].sort((a, b) => a.id - b.id);

/**
 * How long a step waited, from the event that OPENED it (`result: null`) to its
 * newest — both inside this attempt. `emr_gathering` writes three events
 * (waiting -> processing -> terminal); first-to-last is the whole gathering.
 *
 * Attempt-scoped because pairing one attempt's opener with the next attempt's
 * answer invents a duration neither wait had: a consent decision asked 14:14,
 * restarted 14:41 and answered 15:00 measured 46m for a wait that ran 19.
 */
const waitsWithin = (events) => {
  const byStep = new Map();
  for (const event of events) {
    const key = `${event.agenticRunId}:${event.stepName}`;
    byStep.set(key, [...(byStep.get(key) ?? []), event]);
  }
  const waits = new Map();
  for (const [key, group] of byStep) {
    if (group.length < 2) continue;
    const ordered = [...group].sort((a, b) => a.id - b.id);
    const [opened] = ordered;
    if (opened.result != null) continue; // several events, but it never parked
    waits.set(key, { from: opened.createdAt, to: ordered[ordered.length - 1] });
  }
  return waits;
};

/**
 * Closes a step that was still OPEN when its attempt ended. A restart
 * terminates the whole tree, so the execution that was parked on that step no
 * longer exists — nobody is waiting on it and nobody ever will be. Left alone
 * it renders live: a consent pause abandoned an hour ago still reads "Running —
 * Awaiting your decision" under a finished attempt.
 *
 * DERIVED rather than read, even though the backend DOES write a `superseded`
 * event for exactly these steps. It writes it when the ROOT finalizes, which is
 * after the last `run_resumed`, so the event lands in the LAST attempt's bucket
 * — an attempt the step never ran in — and is dropped there as a phantom. The
 * attempt boundary already carries the fact, and carries it from the moment of
 * the restart rather than whenever the root happens to close.
 */
const closeAbandoned = (entries) =>
  entries.map((entry) =>
    entry.step.result == null ? { ...entry, step: { ...entry.step, result: AUDIT_STEP_RESULT.SUPERSEDED } } : entry
  );

/**
 * Splits a run's log into attempts. The first has no `restart` — it is the
 * original run; every later one names the release that re-ran it.
 *
 * Reads `agentStepEvents`, so `run_resumed` never becomes a row: it is the
 * boundary BETWEEN attempts, not an event inside one.
 */
export const runAttempts = (run) => {
  const restarts = restartEvents(run);
  const events = agentStepEvents(run);

  const byAttempt = new Map();
  for (const event of events) {
    const index = attemptOf(event.id, restarts);
    byAttempt.set(index, [...(byAttempt.get(index) ?? []), event]);
  }

  // Every attempt that has a restart OR any step — an attempt whose steps were
  // all superseded still gets its divider, so the restart is never invisible.
  const count = Math.max(restarts.length + 1, ...[...byAttempt.keys()].map((i) => i + 1), 1);
  const attempts = Array.from({ length: count }, (_, index) => {
    const own = byAttempt.get(index) ?? [];
    const waits = waitsWithin(own);
    return {
      index,
      restart: index === 0 ? null : restarts[index - 1] ?? null,
      steps: collapseAttempt(own).map((step) => ({
        step,
        wait: waits.get(`${step.agenticRunId}:${step.stepName}`) ?? null
      }))
    };
  }).filter((attempt) => attempt.restart || attempt.steps.length > 0);

  // The LAST attempt is the live one — its open steps are genuinely open.
  return attempts.map((attempt, index) =>
    index === attempts.length - 1 ? attempt : { ...attempt, steps: closeAbandoned(attempt.steps) }
  );
};
